import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  TextField,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_DIALOG_CONTENT } from '../../theme/v4Compat';
import { notifications, extractApiErrorMessage } from '../../utils';
import { SettingsService } from '../../hooks/SettingsService';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';

interface TranslationCreateModalProps {
  opened: boolean;
  onModalAction: (result: boolean) => void;
}

const TranslationCreateModal: React.FC<any> = ({ opened, onModalAction }: TranslationCreateModalProps) => {
  const styles = sxStyles(useTheme());
  const settingsService = new SettingsService();
  const { t } = useTranslation();

  const [isBusy, setIsBusy] = React.useState(false);

  const [language, setLanguage] = React.useState('');
  const [isoCode, setIsoCode] = React.useState('');

  const closeDialog = (result: boolean) => {
    onModalAction(result);
    clearFields();
  };

  const clearFields = () => {
    setLanguage('');
    setIsoCode('');
  };

  const handleCreateTranslation = () => {
    if (!validated()) {
      return;
    }

    setIsBusy(true);

    settingsService
      .createTranslation(language, isoCode)
      .then(() => {
        notifications.success(t('f-trans-create-ok'));
        closeDialog(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const validated = (): boolean => {
    if (!isoCode) {
      notifications.warning(t('f-val-lang-trans-iso'));
      return false;
    }
    if (!language) {
      notifications.warning(t('f-val-lang-trans'));
      return false;
    }

    return true;
  };

  return (
    <Dialog
      id="addTransDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      sx={styles.dialog}
      maxWidth="sm"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="addTransDialogTitle" sx={styles.dialogHeaderFooter}>
        {t('f-trans-create-title')}
      </DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container direction="column" spacing={2} sx={styles.updateForm}>
          <Grid item container md={12} spacing={2}>
            <Grid item md={12}>
              <Alert severity="info">{t('f-trans-create-alert')}</Alert>
            </Grid>

            <Grid item md={5} xs={12}>
              <TextField
                label={t('f-trans-lang-iso')}
                variant="outlined"
                type="text"
                fullWidth
                value={isoCode}
                onChange={(e) => {
                  setIsoCode(e.target.value);
                }}
              />
            </Grid>

            <Grid item md={7} xs={12}>
              <TextField
                label={t('f-settings-lang')}
                variant="outlined"
                type="text"
                fullWidth
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={styles.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button
          onClick={() => closeDialog(false)}
          variant="outlined"
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={handleCreateTranslation}
          id="saveLocationBtn"
          disabled={isBusy}
          startIcon={<Icon>{'done_outline'}</Icon>}
          variant="contained"
          color="primary"
        >
          {t('f-trans-create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TranslationCreateModal;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) => {
  const fieldMaxWidth = 220;
  return {
    formControl: {
      margin: theme.spacing(1),
      minWidth: fieldMaxWidth
    },
    dialogHeaderFooter: {
      background: colors.defaultGrey
    },
    dialog: {
      overflow: 'hidden'
    },
    updateForm: {
      minWidth: 250,
      justifyContent: 'center'
    }
  } as const;
};
